import * as React from 'react';

import { flatMap, noop } from 'lodash';

export type StartPollingAction = () => void;
export type StopPollingAction = () => void;

export type PollingContextActionProps = {
  startPolling: StartPollingAction;
  stopPolling: StopPollingAction;
};

export type PollingContextSubscriptionProps = {
  registerSubscription: (subscription: PollingContextSubscription) => void;
  unregisterSubscription: (id: string) => void;
};

export type PollingContextProps = {
  actions: PollingContextActionProps;
  subscriptions: PollingContextSubscriptionProps;
};

export const PollingContext = React.createContext<PollingContextProps>({
  actions: {
    startPolling: noop,
    stopPolling: noop,
  },
  subscriptions: {
    registerSubscription: noop,
    unregisterSubscription: noop,
  },
});

export type PollingContextSubscription = {
  startActions: StartPollingAction[];
  stopActions: StopPollingAction[];
  id: string;
};

export interface PollingContextProviderState {
  subscriptions: { [id: string]: PollingContextSubscription };
}

export interface PollingContextProviderProps {}

export class PollingContextProvider extends React.PureComponent<
  React.PropsWithChildren<PollingContextProviderProps>,
  PollingContextProviderState
> {
  state: PollingContextProviderState = {
    subscriptions: {},
  };

  registerSubscription = (subscription: PollingContextSubscription) => {
    this.setState(state => ({
      subscriptions: {
        ...state.subscriptions,
        [subscription.id]: subscription,
      },
    }));
  };

  unregisterSubscription = (id: string) => {
    this.setState(state => {
      const { [id]: omit, ...subscriptions } = state.subscriptions;

      return { subscriptions };
    });
  };

  startPolling = () => {
    flatMap(this.state.subscriptions, sub => sub.startActions)
      .filter(Boolean)
      .forEach(startPolling => startPolling());
  };

  stopPolling = () => {
    flatMap(this.state.subscriptions, sub => sub.stopActions)
      .filter(Boolean)
      .forEach(stopPolling => stopPolling());
  };

  render() {
    const value = {
      actions: {
        startPolling: this.startPolling,
        stopPolling: this.stopPolling,
      },
      subscriptions: {
        registerSubscription: this.registerSubscription,
        unregisterSubscription: this.unregisterSubscription,
      },
    };

    return <PollingContext.Provider value={value}>{this.props.children}</PollingContext.Provider>;
  }
}
